import {createRow, type RowModel, type Table} from '@tanstack/react-table'

import type {MemexItemModel} from '../../../models/memex-item-model'
import type {SubIssuesByParentId} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {getRowId} from '../helpers/get-row-id'
import type {Row} from '../helpers/types'

/**
 * Custom getSubRows function that reads pre-grouped sub-issue items data from the query cache
 * and recursively creates sub-issue subrows for the given row.
 *
 * @param parentRow The parent row for which to get sub-issue sub-rows
 * @param subIssuesByParentId A mapping of parent issue IDs to their corresponding sub-issue items
 * @param table The TanStack Table instance
 *
 * @returns An array of sub-rows representing the sub-issues of the given parent row
 */

export const getSubIssueSubRows = (
  parentRow: Row<MemexItemModel>,
  subIssuesByParentId: SubIssuesByParentId,
  table: Table<MemexItemModel>,
  rowModel: RowModel<MemexItemModel>,
) => {
  // Get sub-issue items available from the query cache by the row issue's id.
  const items = subIssuesByParentId[parentRow.original.content.id] ?? []

  return items.map((item, index) => {
    // Create a row id that is unique within the context of the row's ancestors.
    // Built recursively in the format of `${parentRowId}-${itemId}`.
    const newRowId = getRowId(item, index, {id: parentRow.id})
    const newRow = createRow<MemexItemModel>(table, newRowId, item, index, parentRow.depth + 1, undefined, parentRow.id)
    rowModel.flatRows.push(newRow)
    // Recursively get sub-rows for the new row.
    newRow.subRows = getSubIssueSubRows(newRow, subIssuesByParentId, table, rowModel)
    rowModel.rowsById[newRow.id] = newRow
    return newRow
  })
}
